import React, { useEffect, useRef, useState, useMemo } from "react";
import { IonPage } from "@ionic/react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Tabs,
  Typography,
  Space,
  Row,
  Col,
  Form,
  Input,
  Breadcrumb,
  Result,
  Spin,
  notification,
  Card,
  Modal,
  Tag,
} from "antd";
import { MailOutlined, Loading3QuartersOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment";
import _ from "lodash";
import { showErrMsg } from "../../../utils/error";
import permissionMetaData from "../../../data/permission";
import { isSuper } from "../../../utils/permission";

//
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/GetStarted/SectionHeader";
import Name from "../../../components/General/Name";

//
import useAuthStore from "../../../states/useAuth";
import usePromotionCodeQuery from "../../../hooks/useQuery/usePromotionCode";
import usePromotionCode from "../../../hooks/usePromotionCode";
import Description from "../../../components/General/Description";
import PeriodPicker from "../../../components/General/PeriodPicker";
import dayjs from "../../../utils/day";
import useCompanyStore from "../../../states/useCompany";
import ItemSelectionDrawer from "../../../components/ItemSelectionDrawer";
import useProductQuery from "../../../hooks/useQuery/useProduct";
import VoucherDetailDrawer from "../../../components/PromotionCode/VoucherDetailDrawer";
import Number from "../../../components/General/Number";
import { formatProductWithAvailableQty, getCodeStatus } from "../../../utils/promotionCode";
import useEstamp from "../../../hooks/useEstamp";
import { getPeriodStatus } from "../../../utils/estamp";
import GiftTable from "../../../components/Estamp/GiftTable";
import VoucherTable from "../../../components/PromotionCode/VoucherTable";
import { formatTime } from "../../../utils/time";
import PromotionCodeStatus from "../../../components/Table/PromotionCodeStatus";

const { Title, Text, Paragraph } = Typography;

const SinglePromotionCode = () => {
  const { id } = useParams(); // Get the :id parameter from the URL
  const history = useHistory();
  const location = useLocation();

  //
  const { getMaxShopExpiry } = useCompanyStore();
  const { auth, user } = useAuthStore();
  const { createPromotionCode, updatePromotionCode, deletePromotionCode } = usePromotionCode();
  const { getSingleEstamp } = useEstamp();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  //Form
  const [promotionCodeForm] = Form.useForm();
  const [newPromotionCode, setNewPromotionCode] = useState();

  //
  const [isShowVoucherDrawer, setIsShowVoucherDrawer] = useState(false);
  const [isShowVoucherDetailDrawer, setIsShowVoucherDetailDrawer] = useState(false);

  //
  const [originalPromotionCode, setOriginalPromotionCode] = useState();
  const [promotionCode, setPromotionCode] = useState();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  //code
  const [ongoingPeriod, setOngoingPeriod] = useState();

  const isActionBtnDisabled =
    id == "new"
      ? !c.hasDiff(originalPromotionCode, newPromotionCode)
      : !originalPromotionCode || !c.hasDiff(originalPromotionCode, promotionCode);

  const canEdit = id == "new" || (id != "new" && getCodeStatus(promotionCode) == "pending");

  const now = useMemo(() => moment().toISOString(), []);

  const {
    data: codes = [],
    isFetching: isFetchingCodes,
    error: codesError,
    refetch: refetchCodes,
  } = usePromotionCodeQuery("codes", { "time.collect.end": { $gt: now } });

  const {
    data: singlePromotionCode,
    isFetching: isFetchingSinglePromotionCode,
    error: promotionCodeError,
    refetch: refetchSinglePromotionCode,
  } = usePromotionCodeQuery(
    "code",
    { code: id },
    {
      enabled: !!id && id !== "new",
      staleTime: Infinity, // Prevents refetching on component remounts or when coming back to the page
      cacheTime: 0, // Keeps data fresh only until the component is unmounted
    }
  );

  // Set original and current promotion code whenever new data is fetched
  useEffect(() => {
    setOriginalPromotionCode(singlePromotionCode);
    setPromotionCode(singlePromotionCode);
    // prepare period data if exist
    if (singlePromotionCode?.coin?.period) {
      setOngoingPeriod(singlePromotionCode.coin.period);
    }
  }, [singlePromotionCode]);

  //
  const promotionCodeEndTime = id == "new" ? newPromotionCode?.time?.collect?.end : promotionCode?.time?.collect?.end;
  const {
    data: products = [],
    isFetching: isFetchingProducts,
    refetch: refetchProducts,
  } = useProductQuery(
    {
      type: "voucher",
      companyId: user?.companyId,
    },
    {
      enabled: !!promotionCodeEndTime, // Ensure the query is only enabled when promotionCodeEndTime is defined
    }
  );
  const filteredProducts = useMemo(() => {
    // Map filtered products to include qty.available
    return products.map((product, i) => {
      // Inject qty.available using formatProductWithAvailableQty
      const productWithQty = formatProductWithAvailableQty(product, codes);

      // Add a unique key and return the updated product
      return { ...productWithQty, key: i };
    });
  }, [products, codes]);

  const getEstampPeriod = async () => {
    const result = await getSingleEstamp(user?.companyId, false, false);
    const ongoingPeriod = result.periods?.find((e) => getPeriodStatus(e) === "ongoing");

    if (!ongoingPeriod || !ongoingPeriod.time?.earn?.start || !ongoingPeriod.time?.earn?.end) {
      log.debug("No valid ongoing coin found");
      return Modal.warn({ title: "No valid ongoing coin found" });
    }

    const collectTime = id == "new" ? newPromotionCode?.time?.collect : singlePromotionCode?.time?.collect;
    if (!collectTime || !collectTime.start || !collectTime.end) {
      log.debug("newPromotionCode.time.collect is not fully defined.");
      return Modal.warn({ title: "Please select a valid period" });
    }

    // Check if `collectTime.start` and `collectTime.end` are within `ongoingPeriod.time.earn`
    const isWithinPeriod =
      moment(collectTime.start).isBetween(
        ongoingPeriod.time.earn.start,
        ongoingPeriod.time.earn.end,
        undefined,
        "[]"
      ) &&
      moment(collectTime.end).isBetween(ongoingPeriod.time.earn.start, ongoingPeriod.time.earn.end, undefined, "[]");

    if (!isWithinPeriod) {
      return Modal.warn({ title: "This code is not within any ongoing coin earning period" });
    }

    setOngoingPeriod(ongoingPeriod);
    updatePromotionCodeData({ coin: { periodId: ongoingPeriod.id, amount: 0 } });
  };

  const submitForm = async () => {
    //check form
    try {
      await promotionCodeForm.validateFields();
    } catch (error) {
      log.error(error);
      return notification.error({
        message: "error",
        description: "Some of the input fields are invalid. Please review and save again",
      });
    }

    let before = id == "new" ? newPromotionCode : promotionCode;

    if (!before?.products?.length > 0) {
      return notification.error({ message: "Add at least one voucher" });
    }

    // Get the diff between originalPromotionCode and request
    let diff = c.getObjectDiff(originalPromotionCode, before);
    console.log("Changed fields:", diff);

    if (!diff) {
      return notification.error({ message: "no information changed" });
    }

    // axios
    try {
      setIsLoadingSubmit(true);

      let response;
      if (id === "new") {
        response = await createPromotionCode(diff);
      } else {
        response = await updatePromotionCode(before.code, diff);
      }

      // Update data
      setOriginalPromotionCode(response);
      setPromotionCode(response);

      // Show success notification
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
      });

      //redirect
      if (id === "new") {
        history.replace(`/merchantUser/promotionCode/${response.code}`);
      }
    } catch (error) {
      let message = error.response ? error.response.data : error.message;
      if (message.includes("Insufficient quota")) {
        notification.error({
          message: (
            <Title style={{ margin: 0 }} level={4}>
              Insufficient account quota
            </Title>
          ),
          description: <Text strong>Please contact us for more quota</Text>,
        });
      } else {
        showErrMsg(error);
      }
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  const updatePromotionCodeData = (newData) => {
    log.debug(newData);

    const update = (prev) => {
      // Create a copy of the previous shop data
      let updated = _.merge({}, prev, newData);

      // overwrite
      if ("products" in newData) {
        updated.products = newData?.products;
      }

      log.debug(updated);
      return updated;
    };

    if (id == "new") {
      setNewPromotionCode(update);
    } else {
      setPromotionCode(update);
    }
  };

  const deleteCode = async () => {
    try {
      setIsLoadingSubmit(true);

      let response = await deletePromotionCode(promotionCode?.code);

      // Update data
      setOriginalPromotionCode(response);
      setPromotionCode(response);

      // Show success notification
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Delete Success
          </Title>
        ),
      });

      //redirect
      history.goBack();
    } catch (error) {
      showErrMsg(error);
    } finally {
      setIsLoadingSubmit(false); // Ensure loading state is cleared
    }
  };

  return (
    <div style={{ margin: "0 auto" }}>
      <Spin
        spinning={isFetchingSinglePromotionCode}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {promotionCodeError ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/merchantUser/promotionCode");
                    }}
                  >
                    Code List
                  </a>
                ),
              },
              {
                title: <a>{id == "new" ? "New Code" : "Code Detail"}</a>,
              },
            ]}
          />

          <PageHeader
            title={id == "new" ? "New Code" : "Code Detail"}
            isSticky
            actionGroup={
              <Space>
                {id != "new" && getCodeStatus(promotionCode) == "pending" ? (
                  <Button
                    size="large"
                    loading={isLoadingSubmit}
                    color="danger"
                    variant="text"
                    style={{ marginRight: "1.5rem", width: "7.5rem" }}
                    onClick={() => {
                      Modal.confirm({
                        title: "Confirm Delete?",
                        okText: "Yes",
                        cancelText: "No",
                        onOk: async () => {
                          deleteCode();
                        },
                      });
                    }}
                  >
                    Delete Code
                  </Button>
                ) : null}

                <Button
                  size="large"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  color="default"
                  variant="filled"
                  style={{ marginRight: "1.5rem", width: "7.5rem" }}
                  onClick={async () => {
                    //reset
                    if (id == "new") {
                      setNewPromotionCode(originalPromotionCode);
                    } else {
                      setPromotionCode(originalPromotionCode);
                    }
                  }}
                >
                  Reset
                </Button>

                <Button
                  size="large"
                  type="primary"
                  disabled={isActionBtnDisabled}
                  loading={isLoadingSubmit}
                  style={{ width: "7.5rem" }}
                  onClick={() => {
                    submitForm();
                  }}
                >
                  {id == "new" ? "Create Code" : "Update"}
                </Button>
              </Space>
            }
            onBack={() => {
              history.goBack();
            }}
          />

          <Form form={promotionCodeForm} style={{ width: "100%", marginTop: "2rem" }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Row align="top" style={{}}>
                  <Col span={4} style={{ fontSize: "1rem" }}>
                    Code
                  </Col>
                  <Col span={20}>
                    {id == "new" ? (
                      <Name
                        state={{
                          data: newPromotionCode?.code,
                          form: promotionCodeForm,
                          keyName: "code",
                          rules: [
                            { required: true, message: "required" },
                            {
                              pattern: /^[A-Za-z0-9]{1,6}$/, // Only letters and digits, up to 6 characters
                              message: "Only letters and digits are allowed, no spaces",
                            },
                            {
                              validator: (_, value) =>
                                value && value.length === 6
                                  ? Promise.resolve()
                                  : Promise.reject(new Error("Must be exactly 6 digits or letters")),
                            },
                          ],
                          onChange: ({ name }) => updatePromotionCodeData({ code: name.toUpperCase() }), // Force uppercase
                        }}
                        readOnly={false}
                        minLength={6}
                        maxLength={6}
                        placeholder="Enter 6 letters or digits"
                        showCount
                        style={{ width: "100%" }}
                      />
                    ) : (
                      <Title level={4} style={{ margin: 0 }}>
                        {promotionCode?.code}
                      </Title>
                    )}
                  </Col>
                </Row>
                <Row align="top" style={{ marginBottom: "2rem" }}>
                  <Col span={4} style={{ fontSize: "1rem" }}>
                    Description
                  </Col>
                  <Col span={20}>
                    <Description
                      state={{
                        data: id == "new" ? newPromotionCode?.description : promotionCode?.description,
                        form: promotionCodeForm,
                        keyName: "description",
                        onChange: updatePromotionCodeData,
                      }}
                      readOnly={false}
                      placeholder={"optional, this will not display on mobile"}
                      row={2}
                    />
                  </Col>
                </Row>

                {/* Voucher */}
                {id == "new" && (!newPromotionCode?.qty > 0 || !newPromotionCode?.time?.collect) ? null : (
                  <Vouchers
                    state={{
                      data: id === "new" ? newPromotionCode?.products : promotionCode?.products,
                      form: promotionCodeForm,
                      onChange: updatePromotionCodeData,
                    }}
                    products={filteredProducts}
                    promotionCodeEndTime={promotionCodeEndTime}
                    codeQty={id === "new" ? newPromotionCode?.qty : promotionCode?.qty}
                    canEdit={canEdit}
                    isFetchingProducts={isFetchingProducts}
                    isShowVoucherDrawer={isShowVoucherDrawer}
                    isShowVoucherDetailDrawer={isShowVoucherDetailDrawer}
                    setIsShowVoucherDrawer={setIsShowVoucherDrawer}
                    setIsShowVoucherDetailDrawer={setIsShowVoucherDetailDrawer}
                    isLoadingSubmit={isLoadingSubmit}
                    onSave={(updated) => {
                      updatePromotionCodeData({ products: updated });
                      setIsShowVoucherDetailDrawer(false);
                    }}
                    onDelete={(productId) => {
                      // Filter out the product with the given productId
                      const updatedProducts =
                        id === "new"
                          ? newPromotionCode.products.filter((product) => product.id !== productId)
                          : promotionCode.products.filter((product) => product.id !== productId);

                      // Call updatePromotionCodeData to update the state with the new filtered products list
                      updatePromotionCodeData({ products: updatedProducts });
                    }}
                  />
                )}
              </Col>
              <Col span={12}>
                <Card
                  title={
                    <SectionHeader
                      title="Game Rules"
                      description="Each user can use the code ONCE within the period, first come first serve. You can update quota anytime"
                      style={{
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    />
                  }
                  style={{
                    marginBottom: "1rem",
                    border: "2px solid var(--ion-color-step-200)",
                  }}
                >
                  <Row align="top" style={{}}>
                    <Col span={24} style={{ marginRight: "1rem", fontSize: "1rem" }}>
                      Remaining Quota for Promotion Code
                    </Col>
                    <Col span={10}>
                      <Number
                        state={{
                          data: id === "new" ? newPromotionCode?.qty : promotionCode?.qty,
                          form: promotionCodeForm,
                          rules: [
                            { required: true, message: "required" },
                            {
                              validator: (_, value) =>
                                value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                            },
                          ],
                          onChange: (value) => value > 0 && updatePromotionCodeData({ qty: value }),
                        }}
                        min={1}
                        keyName="qty"
                        readOnly={false}
                      />
                    </Col>
                  </Row>

                  <Row align="top" style={{ marginTop: "1rem" }}>
                    <Col span={24} style={{ marginRight: "1rem", fontSize: "1rem" }}>
                      Valid Period
                    </Col>
                    <Col span={24}>
                      {id == "new" ? (
                        <PeriodPicker
                          state={{
                            data: id === "new" ? newPromotionCode?.time?.collect : promotionCode?.time?.collect,
                            form: promotionCodeForm,
                            rules: [{ required: true, message: "required" }],
                            onChange: (updated) => {
                              const currentCollect = (id === "new" ? newPromotionCode : promotionCode)?.time?.collect;

                              // Check if currentCollect has both start and end
                              const hasExistingPeriod = currentCollect?.start && currentCollect?.end;

                              if (hasExistingPeriod) {
                                Modal.confirm({
                                  title: "Confirm?",
                                  content:
                                    "Changing the period will reset the selected products. Do you want to continue?",
                                  okText: "Yes",
                                  cancelText: "No",
                                  onOk: async () => {
                                    updatePromotionCodeData({
                                      time: {
                                        ...((id === "new" ? newPromotionCode : promotionCode)?.time || {}),
                                        collect: updated,
                                      },
                                      coin: undefined,
                                      products: [], // reset products
                                    });

                                    // clear ongoing period
                                    setOngoingPeriod();
                                  },
                                  onCancel: () => {
                                    // Revert UI back to old value
                                    promotionCodeForm.setFieldsValue({
                                      collectPeriod: [dayjs(currentCollect.start), dayjs(currentCollect.end)],
                                    });
                                  },
                                });
                              } else {
                                // No existing period → just update without confirm
                                updatePromotionCodeData({
                                  time: {
                                    ...((id === "new" ? newPromotionCode : promotionCode)?.time || {}),
                                    collect: updated,
                                  },
                                  coin: undefined,
                                });
                                setOngoingPeriod();
                              }
                            },
                            keyName: "collectPeriod",
                          }}
                          minDate={dayjs()}
                          maxDate={getMaxShopExpiry()}
                          style={{ width: "100%" }}
                        />
                      ) : !promotionCode?.time?.collect ? null : (
                        <Text strong>
                          <PromotionCodeStatus code={promotionCode} style={{ marginRight: "1rem" }} />
                          {`${formatTime(promotionCode.time.collect.start, "YYYY-MM-DD")} to ${formatTime(
                            promotionCode.time.collect.end,
                            "YYYY-MM-DD"
                          )}`}
                        </Text>
                      )}
                    </Col>
                  </Row>
                </Card>

                {/* Coin */}
                {id == "new" && !newPromotionCode?.time ? null : (
                  <Card
                    title={<SectionHeader title="Add Coin" description="optional" />}
                    style={{
                      margin: "1rem 0",
                      border: "2px solid var(--ion-color-step-200)",
                    }}
                    extra={
                      <Button
                        size="large"
                        type="primary"
                        disabled={!canEdit}
                        onClick={() => {
                          if (id === "new" && !newPromotionCode?.time) {
                            Modal.warn({ title: "Select a valid period first", maskClosable: true });
                          } else {
                            getEstampPeriod();
                          }
                        }}
                      >
                        Add+
                      </Button>
                    }
                  >
                    {ongoingPeriod ? (
                      <>
                        <Tag color="green" style={{}}>
                          ongoing
                        </Tag>
                        <SectionHeader title={ongoingPeriod.name} description={`$${ongoingPeriod.rate} Earn 1 Coin`} />

                        <Title level={5} style={{ marginTop: "2rem" }}>
                          Amount
                        </Title>
                        <Number
                          state={{
                            data: id === "new" ? newPromotionCode?.coin?.amount : promotionCode?.coin?.amount,
                            form: promotionCodeForm,
                            rules: [
                              { required: true, message: "required" },
                              {
                                validator: (_, value) =>
                                  value > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error("Must be greater than zero")),
                              },
                            ],
                            onChange: (value) => value > 0 && updatePromotionCodeData({ coin: { amount: value } }),
                          }}
                          min={1}
                          keyName="coin.amount"
                          readOnly={!canEdit}
                        />
                      </>
                    ) : null}
                  </Card>
                )}
              </Col>
            </Row>
          </Form>
        </>
      )}
    </div>
  );
};

const Vouchers = ({
  state = {},
  products = [],
  promotionCodeEndTime,
  codeQty,
  canEdit,
  isFetchingProducts,
  isShowVoucherDrawer,
  isShowVoucherDetailDrawer,
  setIsShowVoucherDrawer,
  setIsShowVoucherDetailDrawer,
  isLoadingSubmit,
  onSave,
  onDelete,
}) => {
  const { data = [], form } = state;
  const { id } = useParams(); // Get the :id parameter from the URL
  //
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [voucher, setVoucher] = useState();

  const product = useMemo(() => {
    return selectedProductIds.length > 0 ? products.find((e) => e.id == selectedProductIds[0]) : undefined;
  }, [voucher, selectedProductIds, products]);

  const rowDisablePredicate = (record) => {
    return record.qty.available < codeQty ? { disabled: true, reason: "Insufficient qty" } : { disabled: false };
  };

  const productsToSelect = useMemo(() => {
    return products
      .filter(
        (e) =>
          e?.qty?.available > 0 &&
          !data.map((x) => x.id).includes(e.id) &&
          !e.isReferral &&
          moment(e?.time?.redeem?.end).isSameOrAfter(moment(promotionCodeEndTime))
      )
      .sort((a, b) => {
        // disabled items go to bottom
        const aDisabled = rowDisablePredicate(a).disabled ? 1 : 0;
        const bDisabled = rowDisablePredicate(b).disabled ? 1 : 0;
        return aDisabled - bDisabled;
      });
  }, [data, products, promotionCodeEndTime]);

  const tableData = useMemo(() => {
    return data.map((voucher) => {
      // Find the product matching the voucher's productId
      const product = products.find((prod) => prod.id === voucher.id);

      // Return the product along with qtyPerCollection (from the voucher) added to it
      return {
        ...product, // Include all product properties
        qtyPerCollection: voucher.qtyPerCollection || 0, // Add qtyPerCollection from voucher (default to 0 if undefined)
      };
    });
  }, [products, data]); // Recalculate whenever products or data changes

  const onSelectChange = (productIds) => {
    setSelectedProductIds(productIds); // Update local state
  };

  const onConfirm = () => {
    const defaultVoucher = {
      id: selectedProductIds[0],
      qtyPerCollection: 0,
    };

    setVoucher(defaultVoucher);
    setIsShowVoucherDrawer(false);
    setIsShowVoucherDetailDrawer(true);
  };

  return (
    <>
      <ItemSelectionDrawer
        state={{
          data: selectedProductIds,
          form: form,
          onChange: onSelectChange,
        }}
        header={
          <SectionHeader
            title="Select Voucher"
            description={"Only voucher can be selected"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        items={productsToSelect} // Use filtered products instead of all products
        isShow={isShowVoucherDrawer}
        isLoading={isLoadingSubmit}
        isSingleSelect={true}
        tableType="product"
        confirmBtnText={"Next"}
        isConfirmBtnDisabled={selectedProductIds.length === 0}
        onClose={() => {
          //clear
          setSelectedProductIds([]);
          setIsShowVoucherDrawer(false);
        }}
        onConfirm={onConfirm}
        rowDisablePredicate={rowDisablePredicate}
      />

      <VoucherDetailDrawer
        open={isShowVoucherDetailDrawer}
        isLoading={isLoadingSubmit}
        voucher={voucher}
        product={product}
        vouchers={data}
        codeQty={codeQty}
        header={
          <SectionHeader
            title="Voucher Detail"
            description={"Provide detail of the voucher"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        confirmBtnText="Submit"
        onClose={() => {
          setVoucher();
          setIsShowVoucherDetailDrawer(false);
        }}
        onConfirm={(voucher) => {
          const existingIndex = data?.findIndex((item) => item.id === voucher.id);
          let updated = [...(data || [])];
          if (existingIndex !== -1) {
            updated[existingIndex] = voucher; // Replace the existing element
          } else {
            updated.push(voucher); // If it doesn't exist, add the new gift to the end of the array
          }

          //clear
          setSelectedProductIds([]);
          //
          onSave(updated);
        }}
      />

      <Card
        title={<SectionHeader title="Add Voucher" description="At least 1 type of voucher" />}
        style={{
          margin: "4rem 0 1rem 0",
          border: "2px solid var(--ion-color-step-200)",
        }}
        extra={
          <Button
            size="large"
            type="primary"
            loading={isFetchingProducts}
            disabled={!canEdit}
            onClick={() => {
              if (id === "new" && !codeQty > 0) {
                Modal.warn({ title: "Select a quota first", maskClosable: true });
              } else {
                setIsShowVoucherDrawer(true);
              }
            }}
          >
            Add+
          </Button>
        }
      >
        <VoucherTable
          data={tableData}
          codeQty={codeQty}
          canDelete={canEdit}
          loading={isFetchingProducts}
          onDelete={onDelete}
        />
      </Card>
    </>
  );
};

export default SinglePromotionCode;
